import { useState } from "react";
import { Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DatePick } from "@/components/atas/DatePick";
import { errMsg, useApi } from "@/lib/api";
import { tarikh } from "@/pages/AkaunPage";

const inp = "w-full h-10 px-3 rounded-lg bg-bg border border-line text-sm focus:outline-none focus:border-[var(--q)]";

export const BonusDialog = ({ user, onClose, onSaved }) => {
  const api = useApi();
  const [jumlah, setJumlah] = useState(10);
  const [tamat, setTamat] = useState("");
  const [nota, setNota] = useState("");
  const [busy, setBusy] = useState(false);
  const [usage, setUsage] = useState(null);
  const save = async () => {
    setBusy(true);
    try {
      const a = await api("post", `/admin/billing/users/${user.user_id}/bonus`, { jumlah: Number(jumlah), tamat: tamat || null, nota });
      onSaved(user.user_id, a); toast.success(`${jumlah} kuota tambahan diberikan`); setNota("");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const revoke = async (bid) => {
    try { onSaved(user.user_id, await api("delete", `/admin/billing/users/${user.user_id}/bonus/${bid}`)); toast.success("Kuota tambahan dibatalkan"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const loadUsage = async () => setUsage(await api("get", `/admin/billing/users/${user.user_id}/usage`));
  if (!user) return null;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto" data-testid="bonus-dialog">
        <DialogHeader><DialogTitle>{user.email}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="eyebrow">Beri kuota tambahan percuma</div>
          <div className="grid grid-cols-[100px_1fr] gap-3 items-center">
            <input type="number" min="1" value={jumlah} onChange={(e) => setJumlah(e.target.value)} className={inp} data-testid="bonus-amount" />
            <DatePick value={tamat} onChange={setTamat} placeholder="Tiada tarikh tamat" testid="bonus-expiry" />
          </div>
          <input value={nota} onChange={(e) => setNota(e.target.value)} placeholder="Nota (pilihan), cth. peserta TOTIQ" className={inp} data-testid="bonus-note" />
          <button onClick={save} disabled={busy || Number(jumlah) < 1} data-testid="bonus-save"
            className="h-10 px-5 rounded-full bg-ink text-bg text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-40">
            {busy && <Loader2 size={14} className="animate-spin" />} Beri kuota
          </button>
        </div>
        <div className="space-y-2 pt-4 border-t border-line">
          <div className="eyebrow">Kuota tambahan sedia ada</div>
          {user.akaun.bonus_items.length === 0 ? <p className="text-sm text-mute">Tiada.</p> : user.akaun.bonus_items.map((b) => (
            <div key={b.id} className="flex items-center gap-3 text-sm" data-testid={`bonus-item-${b.id}`}>
              <span className="font-mono-data">{b.baki}/{b.jumlah}</span>
              <span className="flex-1 text-mute">{b.nota || "—"} · {b.tamat ? `tamat ${tarikh(b.tamat)}` : "tidak luput"}</span>
              <button onClick={() => revoke(b.id)} aria-label="Batalkan" data-testid={`bonus-revoke-${b.id}`} className="text-mute hover:text-warn"><Trash2 size={14} /></button>
            </div>
          ))}
        </div>
        <div className="space-y-2 pt-4 border-t border-line">
          <button onClick={loadUsage} className="text-sm text-q underline underline-offset-4" data-testid="user-usage-load">Lihat rekod penggunaan</button>
          {usage && (
            <ul className="text-xs space-y-1 font-mono-data" data-testid="user-usage-list">
              {usage.length === 0 && <li className="text-mute">Tiada rekod.</li>}
              {usage.map((u, i) => <li key={i} className="flex gap-2 min-w-0 overflow-hidden"><span className="text-mute">{tarikh(u.created_at)}</span><span>{u.jenis}</span><span className={u.status === "ok" ? "text-i" : "text-warn"}>{u.status}</span><span className="truncate min-w-0 flex-1">{u.rujukan || u.input}</span>{u.sumber_kuota && <span className="text-mute">[{u.sumber_kuota}]</span>}</li>)}
            </ul>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
